import { useStore } from '@/hooks/useStore';
import { File } from '@/types/files';
import { ColumnDef, TableState, Updater } from '@tanstack/react-table';
import Table from '@/components/table/Table';
import { Trash2 } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { t } from 'i18next';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { Form, FormField, FormItem } from '@/components/common/Form';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import yup from '@/services/yup';
import { Input } from '@/components/common/Input';
import { Button } from '@/components/common/Button';
import { flowResult, toJS } from 'mobx';
import { DEFAULT_SELECT_PAGE_SIZES } from '@/configs/constants';
import { format, startOfMonth } from 'date-fns';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import iconCalendar from '../../assets/images/calendar.png';
import { ja } from 'date-fns/locale';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import './index.scss';
import GoogleAPI from '@/services/drive';
import { toastify } from '@/utils/toastify';

function FileManagement() {
    const {
        modalStore: { showAlertModal, hideModal, showErrorModal },
        fileStore: { getListFiles, deleteFileById, listFiles, keepSearchParams, defaultPaging, paging, totalRecord, setObservable },
        apiStore: { setLoading },
        authStore: { logout }
    } = useStore();
    const [searchState, setSearchState] = useState<Object>(keepSearchParams ?? '');
    const [openDatePickerFrom, setOpenDatePickeFrom] = useState<boolean>(false);
    const [openDatePickerTo, setOpenDatePickeTo] = useState<boolean>(false);
    const googleAPIService = GoogleAPI.getInstance();
    type FormData = yup.InferType<typeof validateSchema>;

    const initialValues = {
        file_name: '',
        from_date: '',
        to_date: '',
        customer_id: ''
    };

    useEffect(() => {
        onFetch(initialValues, defaultPaging);
        checkGGToken();
    }, []);

    const checkGGToken = async () => {
        const ggToken = await googleAPIService.getToken();
        if (!ggToken) {
            flowResult(logout());
            showErrorModal({
                content: t('messages.cannot_access_google_drive')
            });
            return;
        }
    };

    const onFetch = (searchParams?: any, paging?: TableState) => {
        if (paging) {
            setObservable('paging', paging ?? null);
        }
        setObservable('keepSearchParams', toJS(searchParams));
        getListFiles(searchParams, paging);
    };

    const handleDeleteFile = (path: string, id: string) => {
        setLoading(true);
        googleAPIService.deleteFileById(path)
            .then(() => {
                return flowResult(deleteFileById(id));
            }).then(() => {
                hideModal();
                toastify('success', t('messages.delete_success'));
                onFetch({
                    ...searchState,
                    from_date: form.getValues('from_date') || '',
                    to_date: form.getValues('to_date') || '',
                    customer_id: form.getValues('customer_id') || '',
                    file_name: form.getValues('file_name') || ''
                }, defaultPaging);
            }).catch(() => {
                hideModal();
                alert(t('messages.error'));
            }).finally(() => {
                setLoading(false);
            });
    };
    const columns: ColumnDef<File>[] = [
        {
            header: 'No',
            size: 50,
            enableSorting: false,
            cell: ({ row: { index } }) => (
                <div>{paging.pagination.pageIndex * paging.pagination.pageSize + index + 1}</div>
            )
        },
        {
            accessorKey: 'name',
            header: t('text.file_name'),
            size: 400,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div
                    onClick={() => {
                        const path = original.path;
                        if (!path) {
                            alert(t('messages.error'));
                            return;
                        }
                        const url = `https://drive.google.com/open?id=${path}`;
                        try {
                            window.open(url, '_blank');
                        } catch (error) {
                            alert(t('messages.error'));
                        }
                    }}
                    className=' text-[15px] text-blue-600 underline text-left cursor-pointer'
                >
                    {original.name}
                </div>
            )
        },
        {
            accessorKey: 'created_at',
            header: t('text.upload_date'),
            size: 100,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <span>
                    <p>{format(original.created_at, 'yyyy/MM/dd')}</p>
                    <p>{format(original.created_at, 'HH:mm:ss')}</p>
                </span>
            )
        },
        {
            header: t('text.action'),
            size: 50,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className=''>
                    <button
                        data-tooltip-id='tootip-delete'
                        onClick={() => showAlertModal({
                            cancelButton: false,
                            saveButton: false,
                            title: t('words_title.confirm'),
                            content: (
                                <div className='h-30 w-full'>
                                    <p className='mt-6 text-[14px] text-black'>{t('messages.confirm_delete')}</p>
                                    <div className='flex flex-row text-center mt-8 w-full justify-end'>
                                        <Button onClick={() => hideModal()} className='min-w-32 bg-gray-300 text-black' variant='outline'>
                                            いいえ
                                        </Button>
                                        <Button onClick={() => handleDeleteFile(original.path, original.id)} className='min-w-32  ml-[20px] bg-red400 text-white' variant='outline'>
                                            はい
                                        </Button>
                                    </div>
                                </div>
                            )
                        })}
                        type='button'
                        className=' p-2 bg-bgUnCheck rounded-full'
                    >
                        <Trash2 className='text-icon' size={16} />
                    </button>
                </div>
            )
        }
    ];
    const validateSchema = yup.object().shape({
        file_name: yup
            .string(),
        from_date: yup.string(),
        to_date: yup.string(),
        customer_id: yup.string()
    });

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: initialValues
    });

    const onChangeSearch = (searchParams: any) => {
        setSearchState(searchParams);
        onFetch({
            ...searchState,
            from_date: form.getValues('from_date') || '',
            to_date: form.getValues('to_date') || '',
            customer_id: form.getValues('customer_id') || '',
            file_name: form.getValues('file_name') || ''
        }, defaultPaging);
    };

    const onStateChange = (stateUpdater: Updater<TableState>) => {
        const newPaging = stateUpdater instanceof Function ? stateUpdater(paging) : stateUpdater;
        onFetch({
            ...searchState,
            from_date: form.getValues('from_date') || '',
            to_date: form.getValues('to_date') || '',
            customer_id: form.getValues('customer_id') || '',
            file_name: form.getValues('file_name') || ''
        }, newPaging);
    };

    return (
        <div>
            <div className='flex flex-row w-full justify-between bg-white p-6 items-center rounded-xl'>
                <Form {...form}>
                    <form className=' w-full justify-between content-center items-center' onSubmit={form.handleSubmit(onChangeSearch)}>
                        <div className='flex space-x-10'>
                            <div className='w-[80vw]'>
                                <div className='flex flex-row w-full justify-between items-center space-x-10'>
                                    <div className='w-1/2 min-w-[250px]'>
                                        <FormField
                                            control={form.control}
                                            name='file_name'
                                            render={({ field }) => (
                                                <FormItem>
                                                    <Input placeholder={t('text.file_name')} type='text' {...field} />
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                    <div className='w-1/2 min-w-[250px]'>
                                        <FormField
                                            control={form.control}
                                            name='customer_id'
                                            render={({ field }) => (
                                                <FormItem>
                                                    <Input placeholder={t('text.customer_id')} type='text' {...field} />
                                                </FormItem>
                                            )}
                                        />
                                    </div>

                                </div>
                                <div className='flex flex-col min-w-[500px] content-start mt-3 '>
                                    <p className='text-[14px] min-w-[100px] text-gray-600'>{t('text.upload_date')}</p>
                                    <div className='flex space-x-10'>
                                        <DropdownMenu.Root open={openDatePickerFrom} onOpenChange={setOpenDatePickeFrom}>
                                            <DropdownMenu.Trigger className={`w-1/2 min-w-[250px] h-10 px-4 py-1 flex justify-between items-center bg-gray-100 rounded-md text-[13px] 
                                    ${(!form.getValues('from_date')) && 'text-txtHolder'}`}
                                            >
                                                <p>{(form.getValues('from_date')) ? `${format(new Date(form.getValues('from_date')!), 'yyyy/MM/dd')}` : t('text.from_date')}</p>
                                                <img src={iconCalendar} className='' alt={iconCalendar} />

                                            </DropdownMenu.Trigger>
                                            <DropdownMenu.Portal>
                                                <DropdownMenu.Content className=''>
                                                    <DayPicker
                                                        mode='single'
                                                        onSelect={(date) => {
                                                            setOpenDatePickeFrom(false);
                                                            if (!date) {
                                                                form.setValue('from_date', '');
                                                                return;
                                                            }
                                                            form.setValue('from_date', format(date, 'yyyy/MM/dd'));
                                                        }}
                                                        className='bg-white rounded-lg shadow-lg p-4 custom-datepicker'
                                                        locale={ja}
                                                        selected={new Date(form.getValues('from_date') || '')}
                                                        month={startOfMonth(new Date(form.getValues('from_date') || new Date()))}
                                                        disabled={form.getValues('to_date') ?
                                                            {
                                                                after: new Date(form.getValues('to_date')!)
                                                            } :
                                                            {
                                                                after: new Date()
                                                            }}
                                                    />
                                                </DropdownMenu.Content>
                                            </DropdownMenu.Portal>
                                        </DropdownMenu.Root>
                                        <DropdownMenu.Root open={openDatePickerTo} onOpenChange={setOpenDatePickeTo}>
                                            <DropdownMenu.Trigger className={`w-1/2 min-w-[250px] h-10 px-4 py-1 flex justify-between items-center bg-gray-100 rounded-md text-[13px] 
                                    ${(!form.getValues('to_date')) && 'text-txtHolder'}`}
                                            >
                                                <p>{(form.getValues('to_date')) ? `${format(new Date(form.getValues('to_date')!), 'yyyy/MM/dd')}` : t('text.to_date')}</p>
                                                <img src={iconCalendar} className='' alt={iconCalendar} />

                                            </DropdownMenu.Trigger>
                                            <DropdownMenu.Portal>
                                                <DropdownMenu.Content className=''>
                                                    <DayPicker
                                                        mode='single'
                                                        onSelect={(date) => {
                                                            setOpenDatePickeTo(false);
                                                            if (!date) {
                                                                form.setValue('to_date', '');
                                                                return;
                                                            }
                                                            form.setValue('to_date', format(date, 'yyyy/MM/dd'));
                                                        }}
                                                        className='bg-white rounded-lg shadow-lg p-4 custom-datepicker'
                                                        locale={ja}
                                                        selected={new Date(form.getValues('to_date') || '')}
                                                        month={startOfMonth(new Date(form.getValues('to_date') || new Date()))}
                                                        disabled={
                                                            form.getValues('from_date') ?
                                                                {
                                                                    before: new Date(form.getValues('from_date')!),
                                                                    after: new Date()
                                                                } :
                                                                { after: new Date() }
                                                        }
                                                    />
                                                </DropdownMenu.Content>
                                            </DropdownMenu.Portal>
                                        </DropdownMenu.Root>
                                    </div>

                                </div>
                            </div>

                            <div className='w-[20vw] flex justify-center items-center'>
                                <Button variant='second' type='submit' className='w-[150px] text-white rounded-lg '>{t('words_title.search')}</Button>
                            </div>
                        </div>

                    </form>
                </Form>
            </div>
            <div className='bg-white rounded-xl mt-6 p-6'>
                <Table
                    columns={columns}
                    data={listFiles}
                    rowCount={totalRecord}
                    onStateChange={onStateChange}
                    state={paging}
                    selectPageSizes={DEFAULT_SELECT_PAGE_SIZES}
                    getRowId={row => row.id?.toString()}
                    cellClassName='border-b-[1px] border-b-bgInput'
                />
            </div>
            <ReactTooltip
                id='tootip-delete'
                place='top'
                content='削除'
            />
        </div>
    );
}

export default observer(FileManagement);
